import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  MessagesSquare, Link2, Unlink, Send, CheckCircle2, XCircle, AlertTriangle, RefreshCw,
} from 'lucide-react';

import GenericSelect from '../../../components/ui/GenericSelect';
import Button from '../../../components/ui/Button';
import IconButton from '../../../components/ui/IconButton';
import Tooltip from '../../../components/ui/Tooltip';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { Badge } from '../config';

// Slack Bağlayıcısı (spec 2026-08-10-slack-baglayicisi-design.md §4, Task 4)
// — Entegrasyonlar sekmesinin ilk (ve v1'de tek) kartı. Kart-grid yerleşimi
// gelecekte Teams/takvim kartları için ayrı bileşen dosyaları eklenmesine
// hazır (bkz. IntegrationsTab.js CardGrid yorumu) — bu dosya yalnız Slack'e
// özgü, paylaşılan bir "IntegrationCard" iskeleti ÇIKARILMADI (v1'de tek
// örnek varken erken soyutlama YAGNI).
const Card = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 22px 24px;
  max-width: 520px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

const IconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4A154B, #611f69);
  color: #fff;
  flex-shrink: 0;
`;

const CardTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TeamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0 18px;
`;

const TeamName = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Description = styled.p`
  font-size: 13px;
  color: #6c757d;
  margin: 4px 0 18px;
  line-height: 1.5;
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const ChannelRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

const ChannelSelectWrap = styled.div`
  flex: 1;
`;

const ReadOnlyChannel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 12px 0;
`;

const Hint = styled.p`
  font-size: 12px;
  color: #6c757d;
  margin: -6px 0 16px;
`;

const ChannelErrorHint = styled.p`
  font-size: 12px;
  color: #B02A37;
  margin: 6px 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const ResultBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 6px;
  margin-top: 14px;
  font-size: 13px;
  ${({ $tone }) => ($tone === 'success' ? `
    background: #DCFCE7;
    color: #16A34A;
  ` : $tone === 'warning' ? `
    background: #FFF7ED;
    color: #EA580C;
  ` : `
    background: #FEF2F2;
    color: #E53935;
  `)}
`;

// Slack Bağlayıcısı Task 4 — bağlı-değil/bağlı iki durum + Test Gönder üç
// sonuçlu inline kutu (WebhooksTab'ın Test Gönder deseninin AYNI 3'lü
// ayrımı — başarı yeşil / Slack'in KENDİ hata kodu sarı / iç-hata kırmızı,
// t3-report.md §3 "İÇ/DIŞ ayrımı webhook'un TestAsync'iyle BİREBİR AYNI").
export default function SlackIntegrationCard({
  status, fetching, canManage,
  channels, channelsFetching, channelsError, onRefreshChannels,
  onConnect, onDisconnect, onSetRecognitionChannel, onTest,
}) {
  const { t } = useTranslation();

  const [connecting, setConnecting] = useState(false);
  const [savingChannel, setSavingChannel] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    await onConnect();
    // Başarılıysa tarayıcı Slack'e yönlendirilir (sayfa TERK EDİLİR) — bu
    // component ayakta kalmaz; başarısızsa (URL alınamadı, ör. oauthFailed)
    // interceptor'ın varsayılan toast'ı zaten gösterildi, yalnız butonu geri aç.
    setConnecting(false);
  }, [onConnect]);

  const handleDisconnectConfirm = useCallback(async () => {
    setDisconnecting(true);
    await onDisconnect();
    setDisconnecting(false);
    setDisconnectConfirm(false);
  }, [onDisconnect]);

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    const result = await onTest();
    setTesting(false);
    setTestResult(result);
  }, [onTest]);

  const channelOptions = useMemo(() => [
    { id: '', name: t('integrations.slack.channelOffOption') },
    ...channels.map((c) => ({ id: c.id, name: c.name })),
  ], [channels, t]);

  const handleChannelChange = useCallback(async (channelId) => {
    const channel = channels.find((c) => c.id === channelId);
    setSavingChannel(true);
    await onSetRecognitionChannel(channelId || null, channel?.name || null);
    setSavingChannel(false);
  }, [channels, onSetRecognitionChannel]);

  // `status.connected` tek doğru kaynak (bkz. config.useSlackIntegrationApi
  // yorumu — GetStatusAsync connected=true iken isEnabled HER ZAMAN true
  // döner, ayrı bir "pasif ama bağlı" görsel durum BE'de YOK); yine de
  // savunmacı: `isEnabled===false` teorik olarak gelirse "Bağlı Değil"
  // rozeti yerine "Pasif" göstererek sessizce yanlış-yeşil rozet RİSKİNİ
  // ELER.
  const connected = !!status?.connected;
  const currentChannelName = status?.recognitionChannelId
    ? (status.recognitionChannelName || status.recognitionChannelId)
    : t('integrations.slack.channelOffOption');

  return (
    <Card>
      <CardHeader>
        <IconWrap><MessagesSquare size={20} /></IconWrap>
        <CardTitle>{t('integrations.slack.cardTitle')}</CardTitle>
      </CardHeader>

      {fetching ? (
        <Description>{t('common.loading')}</Description>
      ) : !connected ? (
        <>
          <Description>{t('integrations.slack.description')}</Description>
          {canManage && (
            <Actions>
              <Button onClick={handleConnect} disabled={connecting} loading={connecting}>
                <Link2 size={14} />
                {connecting ? t('integrations.slack.connecting') : t('integrations.slack.connectButton')}
              </Button>
            </Actions>
          )}
        </>
      ) : (
        <>
          <TeamRow>
            <TeamName data-role="slack-team-name">{status.teamName || status.teamId}</TeamName>
            <Badge
              data-role="slack-status-badge"
              color={status.isEnabled !== false ? '#16A34A' : '#64748B'}
              bg={status.isEnabled !== false ? '#DCFCE7' : '#F1F5F9'}
            >
              {t(status.isEnabled !== false ? 'integrations.slack.statusConnected' : 'integrations.slack.statusDisabled')}
            </Badge>
          </TeamRow>

          <FieldGroup>
            {canManage ? (
              <>
                <ChannelRow>
                  <ChannelSelectWrap>
                    <GenericSelect
                      label={t('integrations.slack.recognitionChannelLabel')}
                      value={status.recognitionChannelId ?? ''}
                      onChange={handleChannelChange}
                      options={channelOptions}
                      idKey="id"
                      labelKey="name"
                    />
                  </ChannelSelectWrap>
                  <Tooltip text={t('integrations.slack.refreshChannels')}>
                    <IconButton
                      type="button"
                      size="sm"
                      onClick={onRefreshChannels}
                      disabled={channelsFetching || savingChannel}
                    >
                      <RefreshCw size={14} />
                    </IconButton>
                  </Tooltip>
                </ChannelRow>
                {channelsError && <ChannelErrorHint data-role="channels-error">{t('integrations.slack.channelsErrorHint')}</ChannelErrorHint>}
                <Hint>{t('integrations.slack.recognitionChannelHint')}</Hint>
              </>
            ) : (
              <ReadOnlyChannel data-role="recognition-channel-readonly">
                {t('integrations.slack.recognitionChannelLabel')}: {currentChannelName}
              </ReadOnlyChannel>
            )}
          </FieldGroup>

          {canManage && (
            <Actions>
              <Button variant="outline" onClick={handleTest} disabled={testing} loading={testing}>
                <Send size={14} />
                {testing ? t('integrations.slack.testing') : t('integrations.slack.testButton')}
              </Button>
              <Button variant="outline" onClick={() => setDisconnectConfirm(true)} disabled={disconnecting}>
                <Unlink size={14} />
                {t('integrations.slack.disconnectButton')}
              </Button>
            </Actions>
          )}

          {testResult && (
            testResult.ok ? (
              testResult.data?.status ? (
                <ResultBox data-role="slack-test-success" $tone="success">
                  <CheckCircle2 size={16} />
                  <span>{t('integrations.slack.testSuccess')}</span>
                </ResultBox>
              ) : (
                <ResultBox data-role="slack-test-warning" $tone="warning">
                  <AlertTriangle size={16} />
                  <span>
                    {t('integrations.slack.testTargetError', {
                      error: testResult.data?.error,
                      defaultValue: `Slack hata döndürdü: ${testResult.data?.error || ''}`,
                    })}
                  </span>
                </ResultBox>
              )
            ) : (
              <ResultBox data-role="slack-test-error" $tone="error">
                <XCircle size={16} />
                <span>{t('integrations.slack.testInternalError')}</span>
              </ResultBox>
            )
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={disconnectConfirm}
        title={t('integrations.slack.disconnectConfirm.title')}
        message={t('integrations.slack.disconnectConfirm.message')}
        onConfirm={handleDisconnectConfirm}
        onCancel={() => setDisconnectConfirm(false)}
        loading={disconnecting}
        type="danger"
      />
    </Card>
  );
}
